import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import EditEnumsDialog from './EditEnumsDialog';
import api from '../lib/api';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

import ProductFilters from './product-management/ProductFilters';
import ProductFormDialog from './product-management/ProductFormDialog';
import EditProductDialog from './product-management/EditProductDialog';
import ProductCard from './product-management/ProductCard';
import ImageGalleryDialog from './product-management/ImageGalleryDialog';
import PrintLabelsDialog from './product-management/PrintLabelsDialog';
import CostHistoryDialog from './product-management/CostHistoryDialog';
import { totalStock, getAgingDays, isForwarding } from './product-management/helpers';

// Max size per uploaded design image
const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB

const ProductManagement = ({ user }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const bulk = useBulkSelect();
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [enums, setEnums] = useState({ categories: [], design_themes: [], sizes: [], colors: [] });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [labelDialogProduct, setLabelDialogProduct] = useState(null);
  const [labelVariants, setLabelVariants] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);
  const [filterOccasions, setFilterOccasions] = useState([]);
  const [filterColors, setFilterColors] = useState([]);
  const [filterSizes, setFilterSizes] = useState([]);
  const [filterStockMin, setFilterStockMin] = useState('');
  const [filterStockMax, setFilterStockMax] = useState('');
  const [filterStockStatuses, setFilterStockStatuses] = useState([]);
  const [filterVendors, setFilterVendors] = useState([]);
  const [filterVendorCities, setFilterVendorCities] = useState([]);
  const [filterAgings, setFilterAgings] = useState([]);
  const [filterFabrics, setFilterFabrics] = useState([]);
  const [filterForwarding, setFilterForwarding] = useState(false);
  const [vendorDesignCodes, setVendorDesignCodes] = useState(null);
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [sortBy, setSortBy] = useState('design_desc');
  const [customColorInput, setCustomColorInput] = useState('');
  const [addingColor, setAddingColor] = useState(false);
  const [imageViewProduct, setImageViewProduct] = useState(null);
  const [costHistoryDialog, setCostHistoryDialog] = useState(null);
  const [costHistoryLoading, setCostHistoryLoading] = useState(false);
  const [editEnumsOpen, setEditEnumsOpen] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [uploadColor, setUploadColor] = useState('');
  const editFileInputRef = useRef(null);
  const [editUploading, setEditUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const editCameraInputRef = useRef(null);
  const galleryImgRef = useRef(null);
  const [imgBaseSize, setImgBaseSize] = useState({ w: 0, h: 0 });

  const [productForm, setProductForm] = useState({
    design_code: '',
    description: '',
    brand_id: '',
    vendor_id: '',
    category: '',
    design_theme: [],
    fabric: '',
    images: [],
    image_colors: {},
    tentative_wsp: '',
    tentative_wsp_groups: [],
  });

  // Edit product state
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductForm, setEditProductForm] = useState({ design_code: '', description: '', brand_id: '', vendor_id: '', category: '', design_theme: [], fabric: '', images: [], image_colors: {} });

  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [newOccasionInput, setNewOccasionInput] = useState('');
  const [newFabricInput, setNewFabricInput] = useState('');
  const [newColorInput, setNewColorInput] = useState('');

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: () => { if (editingProduct) saveEditProduct(); },
    onEscape: () => { if (editingProduct) setEditingProduct(null); else if (isDialogOpen) setIsDialogOpen(false); },
    onNew: () => { if (!isDialogOpen) setIsDialogOpen(true); },
  });

  // Auto-fetch next design number when dialog opens
  useEffect(() => {
    if (isDialogOpen && !productForm.design_code) {
      api.get('/purchase-orders/next-design-number')
        .then(res => setProductForm(p => p.design_code ? p : { ...p, design_code: res.data.design_number }))
        .catch(() => {});
    }
  }, [isDialogOpen]);

  useEffect(() => {
    loadAllData();
  }, []);

  // Fetch PO-linked design codes when vendor filter changes
  useEffect(() => {
    if (filterVendors.length === 0 && filterVendorCities.length === 0) {
      setVendorDesignCodes(null);
      return;
    }
    const vendorIds = new Set(filterVendors);
    if (filterVendorCities.length > 0) {
      vendors.filter(v => filterVendorCities.includes(v.city)).forEach(v => vendorIds.add(v.id));
    }
    if (vendorIds.size === 0) { setVendorDesignCodes(new Set()); return; }
    api.get(`/catalogue/vendor-designs?vendor_id=${[...vendorIds].join(',')}`)
      .then(res => setVendorDesignCodes(new Set(res.data.design_codes || [])))
      .catch(() => setVendorDesignCodes(new Set()));
  }, [filterVendors, filterVendorCities, vendors]);

  const loadAllData = async () => {
    try {
      const [productsRes, brandsRes, vendorsRes, enumsRes] = await Promise.allSettled([
        api.get('/products'),
        api.get('/brands'),
        api.get('/vendors'),
        api.get('/enums'),
      ]);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data);
      else { console.error('Products load failed:', productsRes.reason); toast.error('Failed to load products — retrying...'); }
      if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value.data);
      if (vendorsRes.status === 'fulfilled') setVendors(vendorsRes.value.data);
      if (enumsRes.status === 'fulfilled') setEnums(enumsRes.value.data);
      // Retry products if it failed
      if (productsRes.status === 'rejected') {
        try {
          const retry = await api.get('/products');
          setProducts(retry.data);
        } catch { toast.error('Failed to load catalogue products'); }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // --- Live catalogue updates: refresh on tab focus (A) + "new designs" banner (C) ---
  const [pendingProducts, setPendingProducts] = useState(null);
  const [newDesignsCount, setNewDesignsCount] = useState(0);
  const productsRef = useRef([]);
  const skipRefreshRef = useRef(false);
  useEffect(() => { productsRef.current = products; }, [products]);
  // Don't yank data out from under the user while a dialog/edit is open
  useEffect(() => {
    skipRefreshRef.current = isDialogOpen || !!editingProduct || !!labelDialogProduct
      || !!costHistoryDialog || !!imageViewProduct || editEnumsOpen;
  }, [isDialogOpen, editingProduct, labelDialogProduct, costHistoryDialog, imageViewProduct, editEnumsOpen]);

  const refreshProductsSilent = useCallback(async () => {
    if (skipRefreshRef.current) return;
    try {
      const res = await api.get('/products');
      const latest = res.data || [];
      const currentIds = new Set(productsRef.current.map(p => p.id));
      const newOnes = latest.filter(p => !currentIds.has(p.id));
      if (newOnes.length > 0) {
        // New designs appeared — nudge the user instead of replacing silently
        setPendingProducts(latest);
        setNewDesignsCount(newOnes.length);
      } else {
        // No new designs — silently apply fresh data (stock/price/edits)
        setProducts(latest);
        setPendingProducts(null);
        setNewDesignsCount(0);
      }
    } catch { /* ignore transient errors */ }
  }, []);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') refreshProductsSilent(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refreshProductsSilent]);

  const applyPendingProducts = () => {
    if (pendingProducts) setProducts(pendingProducts);
    setPendingProducts(null);
    setNewDesignsCount(0);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter(f => {
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`"${f.name}" exceeds 2 MB — skipped`); return false; }
      return true;
    });
    if (valid.length === 0) { if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    setUploading(true);
    try {
      for (const file of valid) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/products/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const url = response.data.url;
        setProductForm(prev => ({
          ...prev,
          images: [...prev.images, url],
          image_colors: { ...prev.image_colors, [url]: '' },
        }));
      }
      toast.success(files.length > 1 ? `${files.length} images uploaded!` : 'Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleBulkImageUpload = async (files, formSetter, refInput) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files).filter(f => ['image/jpeg', 'image/png', 'image/webp'].includes(f.type));
    if (fileList.length === 0) { toast.error('No valid images selected'); return; }
    const sized = fileList.filter(f => {
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`"${f.name}" exceeds 2 MB — skipped`); return false; }
      return true;
    });
    if (sized.length === 0) { return; }
    setUploading(true);
    try {
      const formData = new FormData();
      sized.forEach(f => formData.append('files', f));
      const res = await api.post('/products/upload-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const results = res.data.filter(r => r.url);
      if (results.length > 0) {
        formSetter(prev => ({
          ...prev,
          images: [...prev.images, ...results.map(r => r.url)],
          image_colors: {
            ...prev.image_colors,
            ...Object.fromEntries(results.map(r => [r.url, ''])),
          },
        }));
        toast.success(`${results.length} image(s) uploaded!`);
      }
      const errors = res.data.filter(r => r.error);
      if (errors.length > 0) toast.error(`${errors.length} file(s) skipped (unsupported format)`);
    } catch {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      if (refInput?.current) refInput.current.value = '';
    }
  };

  const handleDrop = (e, formSetter, refInput) => {
    e.preventDefault();
    e.stopPropagation();
    handleBulkImageUpload(e.dataTransfer.files, formSetter, refInput);
  };

  const handleCameraCapture = async (e, formSetter) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) { toast.error('Photo exceeds 2 MB'); e.target.value = ''; return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/products/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = response.data.url;
      formSetter(prev => ({
        ...prev,
        images: [...prev.images, url],
        image_colors: { ...prev.image_colors, [url]: '' },
      }));
      toast.success('Photo captured & uploaded!');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Drop incomplete size-pricing groups and coerce prices to numbers.
  const cleanWspGroups = (groups) => (groups || [])
    .filter(g => (g.sizes || []).length > 0 && g.price !== '' && g.price != null && parseFloat(g.price) > 0)
    .map(g => ({ sizes: g.sizes, price: parseFloat(g.price) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.vendor_id) { toast.error('Select a vendor'); return; }
    if (productForm.design_theme.length === 0) { toast.error('Select at least one occasion'); return; }
    try {
      await api.post('/products', {
        ...productForm,
        design_code: productForm.design_code.toUpperCase(),
        design_theme: productForm.design_theme.join(','),
        image_colors: productForm.image_colors || {},
        tentative_wsp: productForm.tentative_wsp === '' || productForm.tentative_wsp == null ? null : parseFloat(productForm.tentative_wsp),
        tentative_wsp_groups: cleanWspGroups(productForm.tentative_wsp_groups),
      });
      toast.success('Product added successfully!');
      setIsDialogOpen(false);
      resetForm();
      loadAllData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create product';
      toast.error(message);
    }
  };

  const deleteProduct = async (productId, designCode) => {
    try {
      await api.delete(`/products/${productId}`);
      toast.success(`${designCode} deleted!`);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const openCostHistory = async (product) => {
    setCostHistoryLoading(true);
    setCostHistoryDialog({ design_code: product.design_code, loading: true });
    try {
      const res = await api.get(`/products/${product.id}/cost-history`);
      setCostHistoryDialog(res.data);
    } catch {
      toast.error('Failed to load cost history');
      setCostHistoryDialog(null);
    } finally { setCostHistoryLoading(false); }
  };

  const resetForm = () => {
    setProductForm({ design_code: '', description: '', brand_id: '', vendor_id: '', category: '', design_theme: [], fabric: '', images: [], image_colors: {}, tentative_wsp: '', tentative_wsp_groups: [] });
    setUploadColor('');
  };

  // Label generation
  const openLabelDialog = (product) => {
    const skus = product.skus || [];
    const colors = [...new Set(skus.map(s => s.color))];
    const sizes = [...new Set(skus.map(s => s.size))];
    const variants = [];
    colors.forEach(color => {
      sizes.forEach(size => {
        const sku = skus.find(s => s.color === color && s.size === size);
        if (sku) {
          variants.push({ color, size, qty: 1, selected: true, sku_code: sku.sku_code });
        }
      });
    });
    setLabelVariants(variants);
    setLabelDialogProduct(product);
  };

  const downloadLabels = async (format = 'pdf') => {
    const selected = labelVariants.filter(v => v.selected && v.qty > 0);
    if (selected.length === 0) { toast.error('Select at least one variant'); return; }
    const endpoint = format === 'excel' ? '/generate-barcode-labels-excel' : '/generate-barcode-labels';
    try {
      const res = await api.post(endpoint, {
        items: [{ product_id: labelDialogProduct.id, variants: selected.map(v => ({ color: v.color, size: v.size, qty: v.qty })) }]
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (format === 'excel') {
        const a = document.createElement('a'); a.href = url; a.download = 'barcode_labels.xlsx'; a.click();
      } else {
        window.open(url, '_blank');
      }
      toast.success(`Labels ${format === 'excel' ? 'downloaded' : 'generated'}!`);
    } catch { toast.error('Failed to generate labels'); }
  };

  const canViewVendor = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.permissions?.can_view_vendor;
  const vendorCities = [...new Set(vendors.map(v => v.city).filter(Boolean))].sort();

  const hasActiveFilters = filterCategories.length > 0 || filterBrands.length > 0 || filterOccasions.length > 0 || filterColors.length > 0 || filterSizes.length > 0 || filterStockStatuses.length > 0 || filterVendors.length > 0 || filterVendorCities.length > 0 || filterAgings.length > 0 || filterFabrics.length > 0 || filterForwarding || filterPriceMin || filterPriceMax || filterStockMin || filterStockMax || searchQuery;

  const clearAllFilters = () => {
    setFilterCategories([]); setFilterBrands([]); setFilterOccasions([]); setFilterColors([]); setFilterSizes([]); setFilterStockStatuses([]); setFilterVendors([]); setFilterVendorCities([]); setFilterAgings([]); setFilterFabrics([]); setFilterForwarding(false); setFilterPriceMin(''); setFilterPriceMax(''); setFilterStockMin(''); setFilterStockMax(''); setSearchQuery('');
  };

  const filteredProducts = products.filter(p => {
    if (filterForwarding && !isForwarding(p)) return false;
    if (filterCategories.length > 0 && !filterCategories.includes(p.category)) return false;
    if (filterBrands.length > 0 && !filterBrands.includes(p.brand_id)) return false;
    if (filterOccasions.length > 0) {
      const productOccasions = (p.design_theme || '').split(',').map(o => o.trim());
      if (!filterOccasions.some(f => productOccasions.includes(f))) return false;
    }
    if (filterFabrics.length > 0 && !filterFabrics.includes(p.fabric)) return false;
    if (filterColors.length > 0) {
      const hasColor = (p.skus || []).some(s => filterColors.includes(s.color));
      if (!hasColor) return false;
    }
    if (filterSizes.length > 0) {
      const hasSize = (p.skus || []).some(s => filterSizes.includes(s.size));
      if (!hasSize) return false;
    }
    if (filterPriceMin) {
      const minWsp = parseFloat(filterPriceMin);
      const hasPrice = (p.skus || []).some(s => s.mrp >= minWsp);
      if (!hasPrice) return false;
    }
    if (filterPriceMax) {
      const maxWsp = parseFloat(filterPriceMax);
      const hasPrice = (p.skus || []).some(s => s.mrp <= maxWsp);
      if (!hasPrice) return false;
    }
    if (filterStockStatuses.length > 0) {
      const stock = (p.skus || []).reduce((sum, s) => sum + (s.current_stock || 0), 0);
      const isInStock = stock > 0;
      const hasReserved = (p.skus || []).some(s => (s.reserved_stock || 0) > 0);
      const wantsInStock = filterStockStatuses.includes('in_stock');
      const wantsOutOfStock = filterStockStatuses.includes('out_of_stock');
      const wantsReserved = filterStockStatuses.includes('reserved');
      if (wantsReserved && !wantsInStock && !wantsOutOfStock) {
        if (!hasReserved) return false;
      } else if (wantsReserved) {
        if (!hasReserved && !((wantsInStock && isInStock) || (wantsOutOfStock && !isInStock))) return false;
      } else {
        if (wantsInStock && !wantsOutOfStock && !isInStock) return false;
        if (wantsOutOfStock && !wantsInStock && isInStock) return false;
      }
    }
    if (filterStockMin) {
      const stock = (p.skus || []).reduce((sum, s) => sum + (s.current_stock || 0), 0);
      if (stock < parseInt(filterStockMin)) return false;
    }
    if (filterStockMax) {
      const stock = (p.skus || []).reduce((sum, s) => sum + (s.current_stock || 0), 0);
      if (stock > parseInt(filterStockMax)) return false;
    }
    if ((filterVendors.length > 0 || filterVendorCities.length > 0) && canViewVendor) {
      // Build the full set of selected vendor IDs (direct + city-based)
      const selectedVendorIds = new Set(filterVendors);
      if (filterVendorCities.length > 0) {
        vendors.filter(v => filterVendorCities.includes(v.city)).forEach(v => selectedVendorIds.add(v.id));
      }
      // Match by direct vendor_id on product OR by PO-linked design codes
      const matchesDirect = p.vendor_id && selectedVendorIds.has(p.vendor_id);
      const matchesPO = vendorDesignCodes && vendorDesignCodes.has(p.design_code);
      if (!matchesDirect && !matchesPO) return false;
    }
    if (filterAgings.length > 0) {
      const days = getAgingDays(p);
      if (days === null) return false;
      const matchesAging = filterAgings.some(bucket => {
        if (bucket === '0-30') return days >= 0 && days <= 30;
        if (bucket === '30-60') return days > 30 && days <= 60;
        if (bucket === '60-90') return days > 60 && days <= 90;
        if (bucket === '90-120') return days > 90 && days <= 120;
        if (bucket === '120+') return days > 120;
        return false;
      });
      if (!matchesAging) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const getVendorName = (vid) => { const v = (vendors || []).find(v => v.id === vid); return v ? (v.name || v.company_name || '') : ''; };
      return p.design_code.toLowerCase().includes(q)
        || (p.category || '').toLowerCase().includes(q)
        || getVendorName(p.vendor_id).toLowerCase().includes(q)
        || (p.brand_id && (brands.find(b => b.id === p.brand_id)?.name || '').toLowerCase().includes(q));
    }
    return true;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'design_asc': return a.design_code.localeCompare(b.design_code, undefined, { numeric: true });
      case 'design_desc': return b.design_code.localeCompare(a.design_code, undefined, { numeric: true });
      case 'last_modified': {
        const da = a.updated_at || a.created_at || a.date_added || '';
        const db2 = b.updated_at || b.created_at || b.date_added || '';
        return new Date(db2) - new Date(da);
      }
      case 'stock_high': return totalStock(b.skus) - totalStock(a.skus);
      case 'stock_low': return totalStock(a.skus) - totalStock(b.skus);
      case 'price_high': {
        const pa = Math.max(...(a.skus || []).map(s => s.mrp || 0), 0);
        const pb = Math.max(...(b.skus || []).map(s => s.mrp || 0), 0);
        return pb - pa;
      }
      case 'price_low': {
        const pa = Math.min(...(a.skus || []).filter(s => s.mrp).map(s => s.mrp), Infinity);
        const pb = Math.min(...(b.skus || []).filter(s => s.mrp).map(s => s.mrp), Infinity);
        return pa - pb;
      }
      case 'newest': return new Date(b.date_added || b.created_at || 0) - new Date(a.date_added || a.created_at || 0);
      case 'oldest': return new Date(a.date_added || a.created_at || 0) - new Date(b.date_added || b.created_at || 0);
      default: return 0;
    }
  });

  // Add custom color
  const addCustomColor = async () => {
    const name = customColorInput.trim();
    if (!name) return;
    setAddingColor(true);
    try {
      await api.post('/enums/colors', { name });
      toast.success(`Color "${name}" added!`);
      setCustomColorInput('');
      const enumsRes = await api.get('/enums');
      setEnums(enumsRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add color'); }
    finally { setAddingColor(false); }
  };

  const addCustomEnum = async (type, name, setter) => {
    if (!name.trim()) return;
    try {
      await api.post(`/enums/${type}`, { name: name.trim() });
      toast.success(`"${name.trim()}" added!`);
      setter('');
      const enumsRes = await api.get('/enums');
      setEnums(enumsRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to add ${type}`); }
  };

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'inventory_staff';
  const canEditProduct = ['admin', 'super_admin', 'manager', 'inventory_staff', 'labelling_staff'].includes(user?.role);
  const canDeleteProduct = user?.role === 'admin' || user?.role === 'super_admin';

  const openEditProduct = (product) => {
    setEditProductForm({
      design_code: product.design_code || '',
      description: product.description || '',
      brand_id: product.brand_id || '',
      vendor_id: product.vendor_id || '',
      category: product.category || '',
      design_theme: (product.design_theme || '').split(',').filter(Boolean),
      fabric: product.fabric || '',
      images: product.images || [],
      image_colors: product.image_colors || {},
      tentative_wsp: product.tentative_wsp ?? '',
      tentative_wsp_groups: product.tentative_wsp_groups || [],
    });
    setEditingProduct(product);
  };

  const saveEditProduct = async () => {
    try {
      await api.put(`/products/${editingProduct.id}`, {
        ...editProductForm,
        design_code: editProductForm.design_code.toUpperCase(),
        design_theme: editProductForm.design_theme.join(','),
        images: editProductForm.images,
        image_colors: editProductForm.image_colors || {},
        tentative_wsp: editProductForm.tentative_wsp === '' || editProductForm.tentative_wsp == null ? null : parseFloat(editProductForm.tentative_wsp),
        tentative_wsp_groups: cleanWspGroups(editProductForm.tentative_wsp_groups),
      });
      toast.success(`Product updated to ${editProductForm.design_code.toUpperCase()}!`);
      setEditingProduct(null);
      loadAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update product'); }
  };

  const renameSkuColor = async (productId, oldColor, newColor) => {
    if (!newColor || newColor === oldColor) return;
    try {
      const res = await api.post(`/products/${productId}/rename-color`, { old_color: oldColor, new_color: newColor });
      toast.success(`Renamed "${oldColor}" → "${newColor}" (stock & orders updated)`);
      setProducts(prev => prev.map(p => (p.id === productId ? res.data : p)));
      setEditingProduct(res.data);
      setEditProductForm(prev => ({ ...prev, image_colors: res.data.image_colors || {} }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Rename failed'); }
  };

  const removeSkuColor = async (productId, color) => {
    if (!window.confirm(`Remove colour "${color}" from this design?`)) return;
    try {
      const res = await api.post(`/products/${productId}/remove-color`, { color });
      toast.success(`Removed colour "${color}"`);
      setProducts(prev => prev.map(p => (p.id === productId ? res.data : p)));
      setEditingProduct(res.data);
      setEditProductForm(prev => ({ ...prev, image_colors: res.data.image_colors || {} }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };

  const handleEditImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter(f => {
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`"${f.name}" exceeds 2 MB — skipped`); return false; }
      return true;
    });
    if (valid.length === 0) { if (editFileInputRef.current) editFileInputRef.current.value = ''; return; }
    setEditUploading(true);
    try {
      for (const file of valid) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/products/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const url = response.data.url;
        setEditProductForm(prev => ({
          ...prev,
          images: [...prev.images, url],
          image_colors: { ...prev.image_colors, [url]: '' },
        }));
      }
      toast.success(files.length > 1 ? `${files.length} images added!` : 'Image added!');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setEditUploading(false);
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="product-management-container">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Catalogue</h1>
          <p className="text-gray-600 mt-1">Manage your garment catalogue — sizes, colors & stock added during Purchase Entry</p>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => setEditEnumsOpen(true)} data-testid="edit-options-button">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              Edit Options
            </Button>
          )}
        </div>
      </div>
      <EditEnumsDialog open={editEnumsOpen} onOpenChange={setEditEnumsOpen} />

      {/* New designs available banner (auto-detected on tab focus) */}
      {newDesignsCount > 0 && (
        <button
          onClick={applyPendingProducts}
          data-testid="new-designs-refresh-banner"
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          {newDesignsCount} new design{newDesignsCount > 1 ? 's' : ''} added — click to refresh
        </button>
      )}

      {/* Filters */}
      <ProductFilters
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        sortBy={sortBy} setSortBy={setSortBy}
        filterCategories={filterCategories} setFilterCategories={setFilterCategories}
        filterBrands={filterBrands} setFilterBrands={setFilterBrands}
        filterOccasions={filterOccasions} setFilterOccasions={setFilterOccasions}
        filterStockStatuses={filterStockStatuses} setFilterStockStatuses={setFilterStockStatuses}
        filterStockMin={filterStockMin} setFilterStockMin={setFilterStockMin}
        filterStockMax={filterStockMax} setFilterStockMax={setFilterStockMax}
        filterFabrics={filterFabrics} setFilterFabrics={setFilterFabrics}
        filterColors={filterColors} setFilterColors={setFilterColors}
        filterSizes={filterSizes} setFilterSizes={setFilterSizes}
        filterPriceMin={filterPriceMin} setFilterPriceMin={setFilterPriceMin}
        filterPriceMax={filterPriceMax} setFilterPriceMax={setFilterPriceMax}
        filterVendors={filterVendors} setFilterVendors={setFilterVendors}
        filterVendorCities={filterVendorCities} setFilterVendorCities={setFilterVendorCities}
        filterAgings={filterAgings} setFilterAgings={setFilterAgings}
        filterForwarding={filterForwarding} setFilterForwarding={setFilterForwarding}
        customColorInput={customColorInput} setCustomColorInput={setCustomColorInput}
        addingColor={addingColor} addCustomColor={addCustomColor}
        hasActiveFilters={hasActiveFilters} clearAllFilters={clearAllFilters}
        enums={enums} brands={brands} vendors={vendors} vendorCities={vendorCities}
        canViewVendor={canViewVendor}
      />

      {/* Catalogue Grid */}
      <Card data-testid="products-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Catalogue ({sortedProducts.length})</CardTitle>
            {canManage && (
              <ProductFormDialog
                isOpen={isDialogOpen} setIsOpen={setIsDialogOpen}
                productForm={productForm} setProductForm={setProductForm}
                handleFormChange={handleFormChange} handleSubmit={handleSubmit}
                handleImageUpload={handleImageUpload}
                handleBulkImageUpload={handleBulkImageUpload}
                handleCameraCapture={handleCameraCapture}
                handleDrop={handleDrop}
                uploading={uploading}
                fileInputRef={fileInputRef} cameraInputRef={cameraInputRef}
                enums={enums} brands={brands} vendors={vendors}
                newColorInput={newColorInput} setNewColorInput={setNewColorInput}
                newCategoryInput={newCategoryInput} setNewCategoryInput={setNewCategoryInput}
                newOccasionInput={newOccasionInput} setNewOccasionInput={setNewOccasionInput}
                newFabricInput={newFabricInput} setNewFabricInput={setNewFabricInput}
                addCustomEnum={addCustomEnum}
                resetForm={resetForm}
              />
            )}
          </div>
          <CardDescription>Your garment catalogue — sizes & stock managed via Purchase Entry</CardDescription>
        </CardHeader>
        <CardContent>
          {sortedProducts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{products.length === 0 ? 'No Items Yet' : 'No items match filters'}</h3>
              <p className="text-gray-600 mb-6">{products.length === 0 ? 'Start building your catalogue by adding your first product.' : 'Try adjusting your search or filters.'}</p>
              {canManage && products.length === 0 && (
                <Button onClick={() => setIsDialogOpen(true)} className="bg-gradient-to-r from-blue-600 to-indigo-600">Add Your First Product</Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {sortedProducts.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  user={user}
                  bulk={bulk}
                  brands={brands}
                  vendors={vendors}
                  expandedProduct={expandedProduct} setExpandedProduct={setExpandedProduct}
                  openCostHistory={openCostHistory}
                  openEditProduct={openEditProduct}
                  deleteProduct={deleteProduct}
                  openLabelDialog={openLabelDialog}
                  setImageViewProduct={setImageViewProduct}
                  setImageZoom={setImageZoom}
                  setGalleryIndex={setGalleryIndex}
                  canEditProduct={canEditProduct}
                  canDeleteProduct={canDeleteProduct}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Product Dialog */}
      <EditProductDialog
        editingProduct={editingProduct} setEditingProduct={setEditingProduct}
        editProductForm={editProductForm} setEditProductForm={setEditProductForm}
        saveEditProduct={saveEditProduct}
        enums={enums} brands={brands} vendors={vendors}
        handleEditImageUpload={handleEditImageUpload}
        handleBulkImageUpload={handleBulkImageUpload}
        handleCameraCapture={handleCameraCapture}
        editUploading={editUploading}
        editFileInputRef={editFileInputRef} editCameraInputRef={editCameraInputRef}
        renameSkuColor={renameSkuColor} removeSkuColor={removeSkuColor}
      />

      {/* Product Image Gallery Dialog */}
      <ImageGalleryDialog
        imageViewProduct={imageViewProduct} setImageViewProduct={setImageViewProduct}
        imageZoom={imageZoom} setImageZoom={setImageZoom}
        galleryIndex={galleryIndex} setGalleryIndex={setGalleryIndex}
        imgBaseSize={imgBaseSize} setImgBaseSize={setImgBaseSize}
        galleryImgRef={galleryImgRef}
        brands={brands}
      />

      {/* Print Labels Dialog */}
      <PrintLabelsDialog
        labelDialogProduct={labelDialogProduct} setLabelDialogProduct={setLabelDialogProduct}
        labelVariants={labelVariants} setLabelVariants={setLabelVariants}
        downloadLabels={downloadLabels}
        brands={brands}
      />

      {/* Cost History Dialog */}
      <CostHistoryDialog
        costHistoryDialog={costHistoryDialog} setCostHistoryDialog={setCostHistoryDialog}
        costHistoryLoading={costHistoryLoading}
      />

      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="PRODUCT" label="designs" onComplete={() => { bulk.clearSelection(); loadAllData(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default ProductManagement;
